/**
 * Rate Limiting Service for AI Chatbot
 * Implements per-tenant and per-session rate limiting
 */

import { dataService } from './dataService';

interface RateLimitEntry {
  count: number;
  resetTime: number;
  lastRequest: number;
}

interface RateLimitConfig {
  tenantLimit: number;      // queries per hour per tenant
  sessionLimit: number;     // queries per 10 minutes per session
  windowMs: number;         // time window in milliseconds
  sessionWindowMs: number;  // session time window in milliseconds
}

export class RateLimitService {
  private config: RateLimitConfig;
  private tenantLimits = new Map<string, RateLimitEntry>();
  private sessionLimits = new Map<string, RateLimitEntry>();

  constructor() {
    this.config = {
      tenantLimit: 50,        // 50 queries per hour per tenant
      sessionLimit: 20,       // 20 queries per 10 minutes per session
      windowMs: 60 * 60 * 1000,      // 1 hour
      sessionWindowMs: 10 * 60 * 1000 // 10 minutes
    };
  }

  /**
   * Check if request is allowed for tenant
   */
  async checkTenantLimit(tenantId: string): Promise<{ allowed: boolean; remaining: number; resetTime: number }> {
    try {
      // Store rate limit data in Firestore for persistence
      const now = Date.now();
      
      // Get current rate limit data from Firestore
      let rateLimitData = await this.getRateLimitFromFirestore(tenantId);
      
      if (!rateLimitData || now > rateLimitData.resetTime) {
        // Reset or initialize rate limit
        rateLimitData = {
          count: 0,
          resetTime: now + this.config.windowMs,
          lastRequest: now
        };
      }

      // Check if limit exceeded
      if (rateLimitData.count >= this.config.tenantLimit) {
        return {
          allowed: false,
          remaining: 0,
          resetTime: rateLimitData.resetTime
        };
      }

      // Update count
      rateLimitData.count += 1;
      rateLimitData.lastRequest = now;

      // Save to Firestore
      await this.saveRateLimitToFirestore(tenantId, rateLimitData);

      return {
        allowed: true,
        remaining: this.config.tenantLimit - rateLimitData.count,
        resetTime: rateLimitData.resetTime
      };

    } catch (error) {
      // If Firestore fails, allow request but log error
      return {
        allowed: true,
        remaining: this.config.tenantLimit,
        resetTime: Date.now() + this.config.windowMs
      };
    }
  }

  /**
   * Check if request is allowed for session
   */
  checkSessionLimit(sessionId: string): { allowed: boolean; remaining: number; resetTime: number } {
    const now = Date.now();
    let sessionData = this.sessionLimits.get(sessionId);

    if (!sessionData || now > sessionData.resetTime) {
      // Reset or initialize session limit
      sessionData = {
        count: 0,
        resetTime: now + this.config.sessionWindowMs,
        lastRequest: now
      };
    }

    // Check if limit exceeded
    if (sessionData.count >= this.config.sessionLimit) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: sessionData.resetTime
      };
    }

    // Update count
    sessionData.count += 1;
    sessionData.lastRequest = now;
    this.sessionLimits.set(sessionId, sessionData);

    return {
      allowed: true,
      remaining: this.config.sessionLimit - sessionData.count,
      resetTime: sessionData.resetTime
    };
  }

  /**
   * Get rate limit status for tenant
   */
  async getTenantStatus(tenantId: string): Promise<{
    remaining: number;
    resetTime: number;
    limit: number;
  }> {
    try {
      const rateLimitData = await this.getRateLimitFromFirestore(tenantId);
      const now = Date.now();

      if (!rateLimitData || now > rateLimitData.resetTime) {
        return {
          remaining: this.config.tenantLimit,
          resetTime: now + this.config.windowMs,
          limit: this.config.tenantLimit
        };
      }

      return {
        remaining: Math.max(0, this.config.tenantLimit - rateLimitData.count),
        resetTime: rateLimitData.resetTime,
        limit: this.config.tenantLimit
      };
    } catch (error) {
      return {
        remaining: this.config.tenantLimit,
        resetTime: Date.now() + this.config.windowMs,
        limit: this.config.tenantLimit
      };
    }
  }

  /**
   * Get rate limit status for session
   */
  getSessionStatus(sessionId: string): {
    remaining: number;
    resetTime: number;
    limit: number;
  } {
    const sessionData = this.sessionLimits.get(sessionId);
    const now = Date.now();

    if (!sessionData || now > sessionData.resetTime) {
      return {
        remaining: this.config.sessionLimit,
        resetTime: now + this.config.sessionWindowMs,
        limit: this.config.sessionLimit
      };
    }

    return {
      remaining: Math.max(0, this.config.sessionLimit - sessionData.count),
      resetTime: sessionData.resetTime,
      limit: this.config.sessionLimit
    };
  }

  /**
   * Reset rate limit for tenant (admin function)
   */
  async resetTenantLimit(tenantId: string): Promise<void> {
    try {
      await dataService.delete(tenantId, 'rateLimits', 'ai_queries');
    } catch (error) {
      // Handle error silently
    }
  }

  /**
   * Reset rate limit for session
   */
  resetSessionLimit(sessionId: string): void {
    this.sessionLimits.delete(sessionId);
  }

  /**
   * Get rate limit data from Firestore
   */
  private async getRateLimitFromFirestore(tenantId: string): Promise<RateLimitEntry | null> {
    try {
      const doc = await dataService.getById(tenantId, 'rateLimits', 'ai_queries');
      if (doc) {
        return {
          count: (doc as any).count || 0,
          resetTime: (doc as any).resetTime || 0,
          lastRequest: (doc as any).lastRequest || 0
        };
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Save rate limit data to Firestore
   */
  private async saveRateLimitToFirestore(tenantId: string, data: RateLimitEntry): Promise<void> {
    try {
      await dataService.create(tenantId, 'rateLimits', {
        id: 'ai_queries',
        count: data.count,
        resetTime: data.resetTime,
        lastRequest: data.lastRequest,
        updatedAt: new Date()
      });
    } catch (error) {
      // Handle error silently
    }
  }

  /**
   * Update rate limit configuration
   */
  updateConfig(newConfig: Partial<RateLimitConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): RateLimitConfig {
    return { ...this.config };
  }

  /**
   * Clean up expired session limits
   */
  cleanup(): void {
    const now = Date.now();
    for (const [sessionId, data] of Array.from(this.sessionLimits.entries())) {
      if (now > data.resetTime) {
        this.sessionLimits.delete(sessionId);
      }
    }
  }

  /**
   * Get statistics
   */
  getStats(): {
    activeTenants: number;
    activeSessions: number;
    config: RateLimitConfig;
  } {
    return {
      activeTenants: this.tenantLimits.size,
      activeSessions: this.sessionLimits.size,
      config: this.getConfig()
    };
  }
}

// Singleton instance
export const rateLimitService = new RateLimitService();

// Clean up expired sessions every 5 minutes
setInterval(() => {
  rateLimitService.cleanup();
}, 5 * 60 * 1000);
